import { useEffect, useState } from "react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { listComerciais, setProposalOwner, type Comercial } from "@/lib/api"

/**
 * Comercial associado à proposta (`created_by`) — reatribuir (ex.: do Mauro
 * para o Henrique). Só admins: muda a capa do PDF, a visibilidade, o vendedor
 * no Odoo e quem assina o email. No solar está no ecrã de edição; AC/BC não
 * têm ecrã de edição, por isso fica no detalhe (pedido da Mais Energia, 02/11).
 */
export function OwnerReassign({
  shell,
  proposalId,
  ownerId,
  disabled,
  onChanged,
}: {
  shell: AppShell | null
  proposalId: string
  ownerId: string | null | undefined
  disabled?: boolean
  onChanged?: () => void
}) {
  const [comerciais, setComerciais] = useState<Comercial[]>([])
  const [value, setValue] = useState(ownerId ?? "")
  const [saving, setSaving] = useState(false)

  useEffect(() => setValue(ownerId ?? ""), [ownerId])
  useEffect(() => {
    if (!shell?.isAdmin) return
    listComerciais(shell).then(setComerciais).catch(() => setComerciais([]))
  }, [shell])

  if (!shell?.isAdmin) return null
  const current = ownerId ?? ""

  const save = async () => {
    if (!value || value === current) return
    setSaving(true)
    try {
      await setProposalOwner(shell, proposalId, value)
      shell.bridge.toast({ variant: "success", description: "Comercial alterado" })
      onChanged?.()
    } catch (e) {
      shell.bridge.toast({ variant: "error", description: e instanceof Error ? e.message : "Falha ao alterar o comercial" })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-1 pt-2">
      <Label className="text-xs text-muted-foreground">Comercial</Label>
      <div className="flex gap-2">
        <Select value={value} onValueChange={setValue} disabled={disabled || saving}>
          <SelectTrigger aria-label="Comercial da proposta">
            <SelectValue placeholder="Selecionar comercial…" />
          </SelectTrigger>
          <SelectContent>
            {current && !comerciais.some((c) => c.id === current) && (
              <SelectItem value={current}>Comercial atual (sem conta ativa)</SelectItem>
            )}
            {comerciais.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name || c.email || c.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={save} disabled={disabled || saving || !value || value === current}>
          {saving ? "A guardar…" : "Guardar"}
        </Button>
      </div>
      {value && value !== current && (
        <p className="text-xs text-amber-600">A proposta passa para este comercial (capa do PDF, lista, vendedor no Odoo e email).</p>
      )}
    </div>
  )
}
